/**
 * Static type effectiveness chart (Gen 6+, 18 types). Baked in so matchups
 * work offline with zero fetches.
 *
 * CHART[attacking][defending] holds only the non-1x multipliers.
 */
export const TYPES = [
  'normal',
  'fire',
  'water',
  'electric',
  'grass',
  'ice',
  'fighting',
  'poison',
  'ground',
  'flying',
  'psychic',
  'bug',
  'rock',
  'ghost',
  'dragon',
  'dark',
  'steel',
  'fairy',
];

const CHART = {
  normal: { rock: 0.5, ghost: 0, steel: 0.5 },
  fire: { fire: 0.5, water: 0.5, grass: 2, ice: 2, bug: 2, rock: 0.5, dragon: 0.5, steel: 2 },
  water: { fire: 2, water: 0.5, grass: 0.5, ground: 2, rock: 2, dragon: 0.5 },
  electric: { water: 2, electric: 0.5, grass: 0.5, ground: 0, flying: 2, dragon: 0.5 },
  grass: {
    fire: 0.5,
    water: 2,
    grass: 0.5,
    poison: 0.5,
    ground: 2,
    flying: 0.5,
    bug: 0.5,
    rock: 2,
    dragon: 0.5,
    steel: 0.5,
  },
  ice: { fire: 0.5, water: 0.5, grass: 2, ice: 0.5, ground: 2, flying: 2, dragon: 2, steel: 0.5 },
  fighting: {
    normal: 2,
    ice: 2,
    poison: 0.5,
    flying: 0.5,
    psychic: 0.5,
    bug: 0.5,
    rock: 2,
    ghost: 0,
    dark: 2,
    steel: 2,
    fairy: 0.5,
  },
  poison: { grass: 2, poison: 0.5, ground: 0.5, rock: 0.5, ghost: 0.5, steel: 0, fairy: 2 },
  ground: { fire: 2, electric: 2, grass: 0.5, poison: 2, flying: 0, bug: 0.5, rock: 2, steel: 2 },
  flying: { electric: 0.5, grass: 2, fighting: 2, bug: 2, rock: 0.5, steel: 0.5 },
  psychic: { fighting: 2, poison: 2, psychic: 0.5, dark: 0, steel: 0.5 },
  bug: {
    fire: 0.5,
    grass: 2,
    fighting: 0.5,
    poison: 0.5,
    flying: 0.5,
    psychic: 2,
    ghost: 0.5,
    dark: 2,
    steel: 0.5,
    fairy: 0.5,
  },
  rock: { fire: 2, ice: 2, fighting: 0.5, ground: 0.5, flying: 2, bug: 2, steel: 0.5 },
  ghost: { normal: 0, psychic: 2, ghost: 2, dark: 0.5 },
  dragon: { dragon: 2, steel: 0.5, fairy: 0 },
  dark: { fighting: 0.5, psychic: 2, ghost: 2, dark: 0.5, fairy: 0.5 },
  steel: { fire: 0.5, water: 0.5, electric: 0.5, ice: 2, rock: 2, steel: 0.5, fairy: 2 },
  fairy: { fire: 0.5, fighting: 2, poison: 0.5, dragon: 2, dark: 2, steel: 0.5 },
};

// Per-era overrides, applied on top of the modern (Gen 6+) chart.
// attacking -> { defending: multiplier }
const PRE_GEN6 = {
  ghost: { steel: 0.5 }, // Steel resisted Ghost/Dark before Gen 6
  dark: { steel: 0.5 },
};
const GEN1_ONLY = {
  bug: { poison: 2 }, // Bug and Poison were mutually super-effective
  poison: { bug: 2 },
  ghost: { psychic: 0 }, // famous Gen 1 bug: Ghost did nothing to Psychic
};

function pairMultiplier(attacking, def, gen) {
  if (gen <= 1 && GEN1_ONLY[attacking] && def in GEN1_ONLY[attacking]) {
    return GEN1_ONLY[attacking][def];
  }
  if (gen < 6 && PRE_GEN6[attacking] && def in PRE_GEN6[attacking]) {
    return PRE_GEN6[attacking][def];
  }
  const row = CHART[attacking] || {};
  return def in row ? row[def] : 1;
}

/** Multiplier of `attacking` type against a defender of `defTypes`. */
export function multiplier(attacking, defTypes, gen = 9) {
  let m = 1;
  for (const def of defTypes) {
    if (gen < 6 && def === 'fairy') continue; // Fairy did not exist
    m *= pairMultiplier(attacking, def, gen);
  }
  return m;
}

/** Per-attacking-type multiplier map against a defender, e.g. { fire: 2, water: 0.5, ... }. */
export function defenseVector(defTypes, gen = 9) {
  const out = {};
  const atk = gen < 6 ? TYPES.filter((t) => t !== 'fairy') : TYPES;
  for (const a of atk) out[a] = multiplier(a, defTypes, gen);
  return out;
}

/**
 * What a Pokémon's STAB types hit, from the attacker's side.
 * Returns { strong: [types any STAB is 2x+ against], walls: [types that
 * resist every one of its STAB types] }.
 */
export function offensiveSummary(atkTypes, gen = 9) {
  const strong = [];
  const walls = [];
  const defs = gen < 6 ? TYPES.filter((t) => t !== 'fairy') : TYPES;
  for (const def of defs) {
    const mults = atkTypes.map((atk) => multiplier(atk, [def], gen));
    if (Math.max(...mults) >= 2) strong.push(def);
    else if (mults.every((m) => m <= 0.5)) walls.push(def);
  }
  return { strong, walls };
}

/**
 * Defensive matchups for a Pokémon, bucketed by multiplier.
 * Returns { '4': [...types], '2': [...], '0.5': [...], '0.25': [...], '0': [...] }
 */
export function defensiveMatchups(defTypes, gen = 9) {
  const buckets = { 4: [], 2: [], 0.5: [], 0.25: [], 0: [] };
  // Types that didn't exist yet can't be attacking: Steel/Dark before Gen 2,
  // Fairy before Gen 6.
  const absent = new Set(gen < 2 ? ['steel', 'dark', 'fairy'] : gen < 6 ? ['fairy'] : []);
  const atkTypes = TYPES.filter((t) => !absent.has(t));
  for (const atk of atkTypes) {
    const m = multiplier(atk, defTypes, gen);
    if (m === 1) continue;
    const key = String(m);
    if (buckets[key]) buckets[key].push(atk);
  }
  return buckets;
}
